/**
 * URL + formatting helpers bridging SoundGuys' flattened `/product/*` routes and
 * DealHunt's nested taxonomy `fullSlug`s, plus small display utilities (price,
 * deal-score tiers, retailer links, card price-signals).
 *
 * SoundGuys URL scheme (decided with the client):
 *   - Product page:  /product/{brand}-{product-name}     (brand-prefixed leaf slug,
 *                                                          mirrors the branded title)
 *   - Category page: /product/{audio}/{headphones}/...   (DealHunt path minus the
 *                                                          `electronics` root)
 * DealHunt fullSlug always begins with the taxonomy root, e.g.
 *   electronics/audio/headphones/over-ear-headphones/bose/headphones-700
 */

import { DEALHUNT_ROOT_SEGMENT, DOMAIN_CURRENCY, DOMAIN_TLD } from './constants'
import type { DealHuntBreadcrumb, DealHuntProductLine } from './types'

/** Build the DealHunt category fullSlug for a SoundGuys category path. */
export function categoryPathToFullSlug(segments: string[]): string {
  return [DEALHUNT_ROOT_SEGMENT, ...segments].join('/')
}

/** SoundGuys category href from a DealHunt fullSlug (drops the root segment). */
export function dealHuntCategoryHref(fullSlug: string, trailingSlash = false): string {
  const parts = fullSlug.split('/').filter(Boolean)
  const rest = parts[0] === DEALHUNT_ROOT_SEGMENT ? parts.slice(1) : parts
  return withSlash(`/product/${rest.join('/')}`, trailingSlash)
}

/**
 * The flattened PDP slug for a product: the DealHunt leaf prefixed with the brand,
 * mirroring `brandedTitle` (skips the prefix when the leaf already starts with the
 * brand). Shared by href generation and URL resolution so the two stay symmetric.
 */
export function productSlug(
  brand: string | null | undefined,
  fullSlug: string,
  /**
   * DealHunt's canonical brand slug (`brandNode.slug` / hit + comparison `brandSlug`).
   * Preferred over `slugify(brand)` when present — it's the same source DealHunt builds the
   * taxonomy leaf from, so the prefix matches exactly and we never lossily slugify a display
   * name ("Bang & Olufsen" → "bang-olufsen"). Falls back to `slugify(brand)` until shipped.
   */
  brandSlug?: string | null
): string {
  const leaf = fullSlug.split('/').filter(Boolean).pop() ?? ''
  const prefix = brandSlug || (brand ? slugify(brand) : '')
  if (!prefix || leaf === prefix || leaf.startsWith(`${prefix}-`)) {
    return leaf
  }
  return `${prefix}-${leaf}`
}

/** SoundGuys product href from a DealHunt fullSlug (flattened to the brand-prefixed leaf). */
export function dealHuntProductHref(
  fullSlug: string,
  brand: string | null | undefined,
  trailingSlash = false,
  brandSlug?: string | null
): string {
  return withSlash(`/product/${productSlug(brand, fullSlug, brandSlug)}`, trailingSlash)
}

/** Normalize a root-relative path to the app's trailing-slash convention. Exported so
 *  canonical builders on static routes match the served URL exactly. */
/** `/compare?products=a,b`: the compare page with these products filled in, in order. A token is
 *  a product slug or a node id (the page redirects ids to slugs). Client-safe here (not in
 *  `./compare`, which loads lab charts and MDAQS server-side) for the review CTA and the lab
 *  drawer. */
export function compareHref(tokens: readonly string[], trailingSlash: boolean): string {
  return `${withSlash('/compare', trailingSlash)}?products=${tokens.join(',')}`
}

/** The flat product slug back out of a `/product/<slug>` href (for `compareHref`); null for a
 *  category href or none. */
export function productSlugFromHref(href: string | null | undefined): string | null {
  return href?.match(/^\/product\/([^/?#]+)\/?$/)?.[1] ?? null
}

export function withSlash(path: string, trailingSlash: boolean): string {
  if (!trailingSlash) {
    return path
  }
  return path.endsWith('/') ? path : `${path}/`
}

// ── Card display helpers (shared by grid cards + search typeahead) ──────────

/** "{brand} {name}", skipping the brand prefix when the name already starts with it. */
export function brandedTitle(brand: string | null | undefined, name: string): string {
  if (brand && name && !name.toLowerCase().startsWith(brand.toLowerCase())) {
    return `${brand} ${name}`
  }
  return name
}

// ── Product-page naming + category trail (one expression each, page file + template) ──

type ProductLineNaming = Pick<DealHuntProductLine, 'node' | 'brandNode'>

/**
 * The product's on-page name — the H1 expression: DealHunt's `displayName` when present,
 * else `brandedTitle(brand, name)`. Every section on the product page (hero, Lab data,
 * Owners, Compare, Videos) and the JSON-LD `Product.name` use this, so the page names the
 * product one way. The `<title>`/meta and the analytics `product_name` deliberately stay
 * on `brandedTitle` (an open naming decision), so they don't call this.
 */
export function productDisplayName(line: ProductLineNaming): string {
  return line.node.displayName ?? brandedTitle(line.brandNode?.name, line.node.name)
}

/** The product page's breadcrumb trail with the final (product) crumb brand-prefixed,
 *  e.g. "QuietComfort 35 (2nd gen)" → "Bose QuietComfort 35 (2nd gen)". Shared by the
 *  visible trail (ProductPageTemplate) and the BreadcrumbList JSON-LD, so they match. */
export function brandedBreadcrumbs(
  line: Pick<DealHuntProductLine, 'breadcrumbs' | 'brandNode'>
): DealHuntBreadcrumb[] {
  const { breadcrumbs, brandNode } = line
  return breadcrumbs.map((crumb, i) => {
    if (i === breadcrumbs.length - 1 && brandNode) {
      return { ...crumb, name: brandedTitle(brandNode.name, crumb.name) }
    }
    return crumb
  })
}

type RelatedLevels = Pick<DealHuntProductLine['node'], 'relatedL1' | 'relatedL2' | 'relatedL3'>

/** The product's deepest category name (`relatedL3 ?? L2 ?? L1`), '' when none — the
 *  `category` value page views and interaction events carry. */
export function productCategoryLeaf(node: RelatedLevels): string {
  return node.relatedL3 ?? node.relatedL2 ?? node.relatedL1 ?? ''
}

/** The product's category trail joined "A > B > C" (empty levels dropped) — JSON-LD
 *  `Product.category`, the analytics `category_path`, and the Meili rail's path filter. */
export function productCategoryPath(node: RelatedLevels): string {
  return [node.relatedL1, node.relatedL2, node.relatedL3].filter(Boolean).join(' > ')
}

/** Deepest leaf of a "A > B > C" category path (e.g. "True Wireless Earbuds"). */
export function categoryLeafLabel(path: string | null | undefined): string | null {
  if (!path) {
    return null
  }
  const parts = path
    .split('>')
    .map(p => p.trim())
    .filter(Boolean)
  return parts.length > 0 ? parts[parts.length - 1] : null
}

/** Display value for a DealHunt spec ("12"+"h" -> "12 h", "active" -> "Active"). */
export function formatSpecValue(spec: {
  specValue: string | null
  specUnit: string | null
}): string {
  const value = (spec.specValue ?? '').trim()
  // Boolean specs arrive as raw "True"/"False" strings from the import pipeline —
  // render them as answers, not Python literals.
  if (/^true$/i.test(value)) {
    return 'Yes'
  }
  if (/^false$/i.test(value)) {
    return 'No'
  }
  const display = /^[a-z]/.test(value) ? value.charAt(0).toUpperCase() + value.slice(1) : value
  return spec.specUnit ? `${display} ${spec.specUnit}` : display
}

/** Slugify a free-text product name for search matching ("Headphones 700" -> "headphones-700"). */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

// ── Marketplace formatting ──────────────────────────────────────────────────
//
// Currency and retailer TLD are derived from the marketplace `domain` via the
// DOMAIN_CURRENCY / DOMAIN_TLD tables (constants.ts). Those mirror DealHunt's
// canonical `DOMAIN_INFO` registry (dealhunt: `shared/schema/enums.ts` — the
// `{ currency, amazonHost }` per marketplace). This is a DELIBERATE frontend copy,
// not an oversight: the API-migration doc classifies price/currency formatting as
// SoundGuys presentation (§E "Do NOT move to the API"), and search/browse hits carry
// only `domain` (no currency) — including the browser-direct Meili rail + typeahead,
// which never touch the DealHunt server, so a server-computed field couldn't reach them.
// (Retailer-link ownership DOES move server-side — see migration doc A3 `buyUrl`, which
// will retire DOMAIN_TLD once shipped.)
//
// The tables cover today's 11 marketplaces. `warnUnknownDomain` fires (once per domain)
// when a NEW marketplace appears: the `?? 'USD'` / `?? 'com'` fallback FABRICATES a price
// / retailer link rather than failing, so drift must be loud, not silent.
const warnedDomains = new Set<string>()
function warnUnknownDomain(domain: string): void {
  if (!domain || warnedDomains.has(domain)) {
    return
  }
  warnedDomains.add(domain)
  console.warn(
    `[dealhunt] no currency/TLD mapping for marketplace domain "${domain}" — falling back ` +
      'to USD/.com. Add it to DOMAIN_CURRENCY + DOMAIN_TLD (constants.ts), mirroring the ' +
      'canonical DOMAIN_INFO registry (dealhunt shared/schema/enums.ts).'
  )
}

export function formatCents(cents: number | null | undefined, domain = 'US'): string | null {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) {
    return null
  }
  const mapped = DOMAIN_CURRENCY[domain]
  if (!mapped) {
    warnUnknownDomain(domain)
  }
  const currency = mapped ?? 'USD'
  const value = cents / 100
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value)
  } catch {
    return `$${value.toFixed(2)}`
  }
}

// ── Retailer / Amazon links ─────────────────────────────────────────────────

export function amazonUrl(asin: string, domain = 'US', shortIoUrl?: string | null): string {
  if (shortIoUrl) {
    return shortIoUrl
  }
  const tld = DOMAIN_TLD[domain]
  if (!tld) {
    warnUnknownDomain(domain)
  }
  return `https://www.amazon.${tld ?? 'com'}/dp/${asin}`
}

// ── Relative time ("2h ago") ────────────────────────────────────────────────

export function relativeTime(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null
  }
  const ts = typeof value === 'number' ? value * 1000 : Date.parse(value)
  if (!Number.isFinite(ts)) {
    return null
  }
  const diffMs = Date.now() - ts
  if (diffMs < 0) {
    return 'just now'
  }
  const mins = Math.floor(diffMs / 60000)
  if (mins < 1) {
    return 'just now'
  }
  if (mins < 60) {
    return `${mins}m ago`
  }
  const hours = Math.floor(mins / 60)
  if (hours < 24) {
    return `${hours}h ago`
  }
  const days = Math.floor(hours / 24)
  if (days < 30) {
    return `${days}d ago`
  }
  return `${Math.floor(days / 30)}mo ago`
}
